import "./system.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { backupService } from "../../firebase/backupService";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box
} from "@mui/material";
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  HealthAndSafety as HealthIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Clear as ClearIcon,
  Dangerous as DangerousIcon
} from "@mui/icons-material";

const System = () => {
  const { user } = useAuth();
  const [systemHealth, setSystemHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [restoreHistory, setRestoreHistory] = useState([]);
  const [clearHistory, setClearHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Backup dialog states
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupName, setBackupName] = useState("");
  const [creatingBackup, setCreatingBackup] = useState(false);

  // Restore dialog states
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState("");
  const [restoreOptions, setRestoreOptions] = useState({
    clearExisting: false,
    collections: ['users', 'apartments', 'payments', 'maintenance', 'notices', 'messages', 'notifications']
  });
  const [restoring, setRestoring] = useState(false);

  // Clear data dialog states
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearOptions, setClearOptions] = useState({
    collections: [],
    preserveAdmins: true,
    preserveSystemData: true
  });
  const [clearing, setClearing] = useState(false);

  // Auto backup dialog states
  const [autoBackupDialogOpen, setAutoBackupDialogOpen] = useState(false);
  const [autoBackupActive, setAutoBackupActive] = useState(false);
  const [autoBackupRunning, setAutoBackupRunning] = useState(false);

  // Available collections for restore
  const availableCollections = [
    { value: 'users', label: 'Users' },
    { value: 'apartments', label: 'Apartments' },
    { value: 'payments', label: 'Payments' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'notices', label: 'Notices' },
    { value: 'messages', label: 'Messages' },
    { value: 'notifications', label: 'Notifications' }
  ];

  // Load system data
  const loadSystemData = async () => {
    setLoading(true);
    try {
      const [healthResult, backupsResult, historyResult, clearHistoryResult] = await Promise.all([
        backupService.getSystemHealth(),
        backupService.getAllBackups(),
        backupService.getRestoreHistory(),
        backupService.getClearHistory()
      ]);

      if (healthResult.success) {
        setSystemHealth(healthResult.data);
      }

      if (backupsResult.success) {
        setBackups(backupsResult.data);
        
        // Check if today's automatic backup exists
        const today = new Date().toISOString().split('T')[0];
        const todayBackup = backupsResult.data.find(backup => 
          backup.metadata.name === `Backup_${today}` && 
          backup.metadata.type === 'automatic'
        );
        setAutoBackupRunning(!!todayBackup);
      }

      if (historyResult.success) {
        setRestoreHistory(historyResult.data);
      }

      if (clearHistoryResult.success) {
        setClearHistory(clearHistoryResult.data);
      }

    } catch (error) {
      console.error('Error loading system data:', error);
      setMessage({ type: "error", text: "Failed to load system data" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      loadSystemData();
    }
  }, [user]);

  // Create backup
  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const result = await backupService.createBackup(backupName || null);
      
      if (result.success) {
        setMessage({ 
          type: "success", 
          text: `Backup created successfully: ${result.metadata.name} (${result.documentCount} documents)` 
        });
        setBackupDialogOpen(false);
        setBackupName("");
        loadSystemData(); // Refresh data
      } else {
        setMessage({ type: "error", text: result.error.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to create backup" });
    } finally {
      setCreatingBackup(false);
    }
  };

  // Restore from backup
  const handleRestore = async () => {
    if (!selectedBackup) {
      setMessage({ type: "error", text: "Please select a backup to restore from" });
      return;
    }

    setRestoring(true);
    try {
      const result = await backupService.restoreFromBackup(selectedBackup, restoreOptions);
      
      if (result.success) {
        setMessage({ 
          type: "success", 
          text: `Restore completed: ${result.totalRestored} documents restored from ${result.backupName}` 
        });
        setRestoreDialogOpen(false);
        setSelectedBackup("");
        setRestoreOptions({
          clearExisting: false,
          collections: ['users', 'apartments', 'payments', 'maintenance', 'notices', 'messages', 'notifications']
        });
        loadSystemData(); // Refresh data
      } else {
        setMessage({ type: "error", text: result.error.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to restore from backup" });
    } finally {
      setRestoring(false);
    }
  };

  // Delete backup
  const handleDeleteBackup = async (backupId, backupName) => {
    if (window.confirm(`Are you sure you want to delete backup "${backupName}"?`)) {
      try {
        const result = await backupService.deleteBackup(backupId);
        if (result.success) {
          setMessage({ type: "success", text: "Backup deleted successfully" });
          loadSystemData(); // Refresh data
        } else {
          setMessage({ type: "error", text: result.error.message });
        }
      } catch (error) {
        setMessage({ type: "error", text: "Failed to delete backup" });
      }
    }
  };

  // Schedule automatic backup
  const handleScheduleBackup = async () => {
    setAutoBackupDialogOpen(true);
  };

  // Execute automatic backup
  const handleExecuteAutoBackup = async () => {
    setAutoBackupActive(true);
    try {
      const result = await backupService.scheduleAutomaticBackup();
      
      if (result.success) {
        setMessage({ 
          type: "success", 
          text: result.message || "Automatic backup scheduled successfully" 
        });
        setAutoBackupDialogOpen(false);
        setAutoBackupRunning(true); // Set as running since backup was created
        loadSystemData(); // Refresh data
      } else {
        setMessage({ type: "error", text: result.error.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to schedule automatic backup" });
    } finally {
      setAutoBackupActive(false);
    }
  };

  // Clear data
  const handleClearData = async () => {
    if (clearOptions.collections.length === 0) {
      setMessage({ type: "error", text: "Please select at least one collection to clear" });
      return;
    }

    setClearing(true);
    try {
      const result = await backupService.clearData(clearOptions.collections, {
        preserveAdmins: clearOptions.preserveAdmins,
        preserveSystemData: clearOptions.preserveSystemData
      });
      
      if (result.success) {
        setMessage({ 
          type: "success", 
          text: `Data cleared: ${result.totalCleared} documents cleared from ${clearOptions.collections.length} collections` 
        });
        setClearDialogOpen(false);
        setClearOptions({
          collections: [],
          preserveAdmins: true,
          preserveSystemData: true
        });
        loadSystemData(); // Refresh data
      } else {
        setMessage({ type: "error", text: result.error.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to clear data" });
    } finally {
      setClearing(false);
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <HealthIcon />;
    }
  };

  // Stat Card Component
  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card className="stat-card">
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box color={color}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (user?.role !== 'admin') {
    return (
      <div className="system">
        <Sidebar />
        <div className="systemContainer">
          <Navbar />
          <div className="content">
            <Alert severity="error">
              Access denied. Only administrators can access system health features.
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="system">
      <Sidebar />
      <div className="systemContainer">
        <Navbar />
        
        {/* Header with action buttons */}
        <Box className="system-header" mb={0}>
          <Typography variant="h4" component="h1">
            System Health & Backup Management
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSystemData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<BackupIcon />}
              onClick={() => setBackupDialogOpen(true)}
            >
              Create Backup
            </Button>
          </Box>
        </Box>

        {message.text && (
          <Alert 
            severity={message.type} 
            onClose={() => setMessage({ type: "", text: "" })}
            sx={{ m: 2 }}
          >
            {message.text}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* System Health Overview Cards */}
            <div className="stat-widgets">
              <StatCard
                title="System Status"
                value={systemHealth?.systemStatus || "Loading..."}
                icon={<HealthIcon />}
                color={systemHealth?.systemStatusColor || "primary.main"}
                subtitle={`Health Score: ${systemHealth?.healthScore || 0}%`}
              />
              <StatCard
                title="Total Documents"
                value={systemHealth?.totalDocuments || 0}
                icon={<StorageIcon />}
                color="info.main"
                subtitle="Across all collections"
              />

              <StatCard
                title="Available Backups"
                value={backups.length}
                icon={<BackupIcon />}
                color={systemHealth?.latestBackup?.status === 'warning' ? "warning.main" : "success.main"}
                subtitle={systemHealth?.latestBackup?.issue || "System backups"}
              />
              <StatCard
                title="Restore Operations"
                value={restoreHistory.length}
                icon={<HistoryIcon />}
                color="warning.main"
                subtitle="Total restores performed"
              />
            </div>

            {/* Quick Actions */}
            <div className="quick-actions">
              <Card className="action-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button
                      variant={autoBackupRunning || autoBackupActive ? "contained" : "outlined"}
                      color={autoBackupRunning ? "success" : autoBackupActive ? "warning" : "primary"}
                      startIcon={<ScheduleIcon />}
                      onClick={handleScheduleBackup}
                      size="small"
                      disabled={autoBackupActive}
                    >
                      {autoBackupActive ? "Auto Backup Active..." : autoBackupRunning ? "Auto Backup Running" : "Auto Backup"}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RestoreIcon />}
                      onClick={() => setRestoreDialogOpen(true)}
                      size="small"
                    >
                      Restore
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<ClearIcon />}
                      onClick={() => setClearDialogOpen(true)}
                      size="small"
                    >
                      Clear Data
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<SecurityIcon />}
                      onClick={loadSystemData}
                      size="small"
                    >
                      Check Health
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </div>



            {/* Collection Health */}
            <div className="listContainer">
              <div className="listTitle">
                Collection Health
              </div>
              {systemHealth && (
                <TableContainer component={Paper} className="table-container">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Collection</TableCell>
                        <TableCell>Document Count</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Issues</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(systemHealth.collections).map(([name, data]) => (
                        <TableRow key={name}>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {name}
                            </Typography>
                          </TableCell>
                          <TableCell>{data.documentCount || 0}</TableCell>
                          <TableCell>
                            {data.lastUpdated ? new Date(data.lastUpdated).toLocaleString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {data.status === 'error' ? (
                              <Chip label="Error" color="error" size="small" />
                            ) : data.status === 'warning' ? (
                              <Chip label="Warning" color="warning" size="small" />
                            ) : (
                              <Chip label="Healthy" color="success" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {data.error && (
                              <Typography variant="caption" color="error">
                                {data.error}
                              </Typography>
                            )}
                            {data.issue && (
                              <Typography variant="caption" color="warning">
                                {data.issue}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>

            {/* Health Issues */}
            {systemHealth?.healthIssues && systemHealth.healthIssues.length > 0 && (
              <div className="listContainer">
                <div className="listTitle">
                  Health Issues
                </div>
                <Box p={2}>
                  {systemHealth.healthIssues.map((issue, index) => (
                    <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                      {issue}
                    </Alert>
                  ))}
                </Box>
              </div>
            )}

            {/* Available Backups */}
            <div className="listContainer">
              <div className="listTitle">
                Available Backups
              </div>
              {backups.length > 0 ? (
                <TableContainer component={Paper} className="table-container">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Backup Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Documents</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backups.map((backup) => (
                        <TableRow key={backup.id}>
                          <TableCell>{backup.metadata.name}</TableCell>
                          <TableCell>
                            <Chip 
                              label={backup.metadata.type} 
                              color={backup.metadata.type === 'manual' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{backup.metadata.totalDocuments}</TableCell>
                          <TableCell>{formatTimestamp(backup.metadata.timestamp)}</TableCell>
                          <TableCell>
                            <Tooltip title="Restore from this backup">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedBackup(backup.id);
                                  setRestoreDialogOpen(true);
                                }}
                              >
                                <RestoreIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete backup">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteBackup(backup.id, backup.metadata.name)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No backups available
                </Typography>
              )}
            </div>

            {/* Restore History */}
            <div className="listContainer">
              <div className="listTitle">
                Restore History
              </div>
              {restoreHistory.length > 0 ? (
                <TableContainer component={Paper} className="table-container">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Backup Name</TableCell>
                        <TableCell>Restored By</TableCell>
                        <TableCell>Documents Restored</TableCell>
                        <TableCell>Restored At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {restoreHistory.map((restore) => (
                        <TableRow key={restore.id}>
                          <TableCell>{restore.backupName}</TableCell>
                          <TableCell>{restore.restoredBy}</TableCell>
                          <TableCell>{restore.totalRestored}</TableCell>
                          <TableCell>{formatTimestamp(restore.restoredAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No restore history available
                </Typography>
              )}
            </div>

            {/* Clear History */}
            <div className="listContainer">
              <div className="listTitle">
                Data Clear History
              </div>
              {clearHistory.length > 0 ? (
                <TableContainer component={Paper} className="table-container">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Collections Cleared</TableCell>
                        <TableCell>Documents Cleared</TableCell>
                        <TableCell>Options</TableCell>
                        <TableCell>Cleared At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clearHistory.map((clear) => (
                        <TableRow key={clear.id}>
                          <TableCell>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {clear.collections.map((collection) => (
                                <Chip key={collection} label={collection} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>{clear.totalCleared}</TableCell>
                          <TableCell>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              {clear.options.preserveAdmins && (
                                <Chip label="Preserve Admins" size="small" color="success" />
                              )}
                              {clear.options.preserveSystemData && (
                                <Chip label="Preserve System Data" size="small" color="info" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{formatTimestamp(clear.clearedAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No clear history available
                </Typography>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Backup Dialog */}
      <Dialog open={backupDialogOpen} onClose={() => setBackupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create System Backup</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Backup Name (Optional)"
            fullWidth
            variant="outlined"
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            placeholder="e.g., Before System Update"
            helperText="Leave empty for automatic naming"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupDialogOpen(false)} disabled={creatingBackup}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreateBackup} 
            variant="contained" 
            disabled={creatingBackup}
            startIcon={creatingBackup ? <CircularProgress size={16} /> : <BackupIcon />}
          >
            {creatingBackup ? 'Creating...' : 'Create Backup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Restore System from Backup</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Select Backup</InputLabel>
                <Select
                  value={selectedBackup}
                  onChange={(e) => setSelectedBackup(e.target.value)}
                  label="Select Backup"
                >
                  {backups.map((backup) => (
                    <MenuItem key={backup.id} value={backup.id}>
                      {backup.metadata.name} ({backup.metadata.totalDocuments} documents)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Restore Options
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={restoreOptions.clearExisting}
                    onChange={(e) => setRestoreOptions({
                      ...restoreOptions,
                      clearExisting: e.target.checked
                    })}
                  />
                }
                label="Clear existing data before restore"
              />
            </Grid>



            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Collections to Restore
              </Typography>
              <Grid container spacing={1}>
                {availableCollections.map((collection) => (
                  <Grid item xs={6} sm={4} key={collection.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={restoreOptions.collections.includes(collection.value)}
                          onChange={(e) => {
                            const newCollections = e.target.checked
                              ? [...restoreOptions.collections, collection.value]
                              : restoreOptions.collections.filter(c => c !== collection.value);
                            setRestoreOptions({
                              ...restoreOptions,
                              collections: newCollections
                            });
                          }}
                        />
                      }
                      label={collection.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
            Cancel
          </Button>
              <Button
            onClick={handleRestore} 
                variant="contained"
            disabled={restoring || !selectedBackup}
            startIcon={restoring ? <CircularProgress size={16} /> : <RestoreIcon />}
          >
            {restoring ? 'Restoring...' : 'Restore'}
              </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Data Dialog */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DangerousIcon color="warning" />
            Clear System Data
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action will permanently delete data from the selected collections. 
              This operation cannot be undone. Please ensure you have a backup before proceeding.
            </Typography>
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Select Collections to Clear
              </Typography>
              <Grid container spacing={1}>
                {availableCollections.map((collection) => (
                  <Grid item xs={6} sm={4} key={collection.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={clearOptions.collections.includes(collection.value)}
                          onChange={(e) => {
                            const newCollections = e.target.checked
                              ? [...clearOptions.collections, collection.value]
                              : clearOptions.collections.filter(c => c !== collection.value);
                            setClearOptions({
                              ...clearOptions,
                              collections: newCollections
                            });
                          }}
                        />
                      }
                      label={collection.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Clear Options
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clearOptions.preserveAdmins}
                    onChange={(e) => setClearOptions({
                      ...clearOptions,
                      preserveAdmins: e.target.checked
                    })}
                  />
                }
                label="Preserve admin users (recommended)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clearOptions.preserveSystemData}
                    onChange={(e) => setClearOptions({
                      ...clearOptions,
                      preserveSystemData: e.target.checked
                    })}
                  />
                }
                label="Preserve system-critical data (recommended)"
              />
            </Grid>

            {clearOptions.collections.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Summary:</strong> Will clear data from {clearOptions.collections.length} collection(s):{' '}
                    {clearOptions.collections.join(', ')}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} disabled={clearing}>
            Cancel
          </Button>
              <Button
            onClick={handleClearData} 
                variant="contained"
            color="warning"
            disabled={clearing || clearOptions.collections.length === 0}
            startIcon={clearing ? <CircularProgress size={16} /> : <ClearIcon />}
          >
            {clearing ? 'Clearing...' : 'Clear Data'}
              </Button>
        </DialogActions>
      </Dialog>

      {/* Auto Backup Dialog */}
      <Dialog open={autoBackupDialogOpen} onClose={() => setAutoBackupDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon color="primary" />
            Auto Backup Configuration
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <ScheduleIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight="medium">
                  Schedule: Daily at Midnight (00:00)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatic backups are created every day at midnight with format: Backup_YYYY-MM-DD
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              <BackupIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight="medium">
                  Collections Backed Up
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  users, apartments, payments, maintenance, notices, messages, notifications
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" gap={2}>
              <CheckCircleIcon color="primary" />
              <Box>
                <Typography variant="h6" fontWeight="medium">
                  Duplicate Prevention
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Only one automatic backup is created per day to prevent duplicates
                </Typography>
              </Box>
            </Box>
            
            {systemHealth?.latestBackup && (
              <Box display="flex" alignItems="center" gap={2}>
                <HistoryIcon color="primary" />
                <Box>
                  <Typography variant="h6" fontWeight="medium">
                    Last Auto Backup
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {systemHealth.latestBackup.metadata.type === 'automatic' 
                      ? `${systemHealth.latestBackup.metadata.name} (${formatTimestamp(systemHealth.latestBackup.metadata.timestamp)})`
                      : 'No automatic backup found today'
                    }
                  </Typography>
                </Box>
                </Box>
              )}
            
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong> Click "Activate Auto Backup" to create today's automatic backup if it doesn't exist yet.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoBackupDialogOpen(false)} disabled={autoBackupActive}>
            Cancel
          </Button>
          <Button 
            onClick={handleExecuteAutoBackup} 
            variant="contained" 
            color="success"
            disabled={autoBackupActive}
            startIcon={autoBackupActive ? <CircularProgress size={16} /> : <ScheduleIcon />}
          >
            {autoBackupActive ? 'Activating...' : 'Activate Auto Backup'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default System; 